import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";

import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { buildShipmentCode, sanitizeShortCode } from "@/lib/codegen";
import { getPresetByValue, defaultLabelSize } from "@/lib/labelSizes";
import { downloadLabelPDF, printLabelPDF } from "@/lib/pdfGenerator";
import { copyToClipboard } from "@/lib/clipboard";

import { CargoPickupCard, WAREHOUSE_OPTIONS as _WH } from "@/components/generator/CargoPickupCard";
import { CodeModeCard } from "@/components/generator/CodeModeCard";
import { CustomerDetailsCard } from "@/components/generator/CustomerDetailsCard";
import { DgBrandCards } from "@/components/generator/DgBrandCards";
import { ExtraFlagsCard } from "@/components/generator/ExtraFlagsCard";
import { GeneratorPreviewActions } from "@/components/generator/GeneratorPreviewActions";
import { LabelSizeCard } from "@/components/generator/LabelSizeCard";
import { PackagesCard } from "@/components/generator/PackagesCard";
import { SalesCard } from "@/components/generator/SalesCard";
import { ShipmentModeCard } from "@/components/generator/ShipmentModeCard";

// Re-export for any external consumers
export const WAREHOUSE_OPTIONS = _WH;

const initialForm = {
  customer_name: "",
  company_name: "",
  mobile_number: "",
  email: "",
  customer_short_code: "",
  trade_lane: "",
  shipment_mode: null,
  has_dg_battery: null,
  has_brand: null,
  is_wooden: null,
  is_protein_supplement: null,
  is_medical: null,
  code_mode: null,
  manual_code: "",
  cargo_lines: [],
  account_of_user_id: "",
  job_coordinator_user_id: "",
  en_no: "",
  commodity: "",
  pickup: null,
  warehouse_location: "",
  sales_person: "",
  sales_amount: "",
  cost: "",
  margin: "",
  sales_currency: "AED",
  total_cbm: "",
  total_chargeable_weight: "",
  label_number: "",
  package_mode: "manual",
  package_total: 1,
  package_number: "",
  label_size: defaultLabelSize(),
};

export default function ShipmentGenerator() {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [salesPersons, setSalesPersons] = useState([]);
  const [users, setUsers] = useState([]);

  // Track the next sequence number per short_code (peeked from server, live)
  const [nextSeq, setNextSeq] = useState(0);

  // Debounced fetch of next sequence whenever short_code changes
  useEffect(() => {
    const cleaned = (form.customer_short_code || "").replace(/[^A-Z0-9]/gi, "").toUpperCase();
    if (!cleaned) {
      setNextSeq(0);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get(`/codes/next-seq?short_code=${encodeURIComponent(cleaned)}`)
        .then(({ data }) => { if (!cancelled) setNextSeq(Number(data.next_seq) || 0); })
        .catch(() => { if (!cancelled) setNextSeq(0); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.customer_short_code]);

  // Derived: live shipment code. Auto = formula from answers + peeked sequence.
  // Manual = user-typed code exactly; formula is not applied.
  const generatedCode = useMemo(() => {
    if (form.code_mode === "manual") {
      return (form.manual_code || "").trim();
    }
    return buildShipmentCode({
      shipmentMode: form.shipment_mode,
      hasDgBattery: form.has_dg_battery,
      hasBrand: form.has_brand,
      shortCode: form.customer_short_code,
      pickup: form.pickup,
      warehouseLocation: form.warehouse_location,
      isWooden: form.is_wooden,
      isProteinSupplement: form.is_protein_supplement,
      isMedical: form.is_medical,
      seq: nextSeq,
    });
  }, [
    form.code_mode,
    form.manual_code,
    form.shipment_mode,
    form.has_dg_battery,
    form.has_brand,
    form.customer_short_code,
    form.pickup,
    form.warehouse_location,
    form.is_wooden,
    form.is_protein_supplement,
    form.is_medical,
    nextSeq,
  ]);

  useEffect(() => {
    let cancelled = false;
    api.get("/meta/sales-persons")
      .then(({ data }) => { if (!cancelled) setSalesPersons(data || []); })
      .catch(() => { if (!cancelled) setSalesPersons([]); });
    api.get("/meta/users")
      .then(({ data }) => { if (!cancelled) setUsers(data || []); })
      .catch(() => { if (!cancelled) setUsers([]); });
    return () => { cancelled = true; };
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleShortCodeChange = (e) =>
    setField("customer_short_code", sanitizeShortCode(e.target.value));

  const handlePresetChange = (value) => {
    const p = getPresetByValue(value);
    setField("label_size", {
      preset: value,
      width_mm: p.width_mm,
      height_mm: p.height_mm,
    });
  };

  const handleCustomSize = (key, val) => {
    const num = Number(val) || 0;
    setField("label_size", { ...form.label_size, preset: "custom", [key]: num });
  };

  const isFormValid = () => {
    if (!form.trade_lane) return "Select a trade lane";
    if (!form.customer_name.trim()) return "Customer name is required";
    if (!form.company_name.trim()) return "Company name is required";
    if (!form.mobile_number.trim()) return "Mobile number is required";
    if (!form.email.trim()) return "Email is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Enter a valid email";
    if (!form.customer_short_code) return "Customer short code is required";
    if (!form.shipment_mode) return "Select shipment mode";
    // These questions drive the AUTO code formula, so they're only required in auto mode.
    if (form.code_mode !== "manual") {
      if (form.has_dg_battery === null) return "Answer DG/Battery question";
      if (form.has_brand === null) return "Answer Brand question";
      if (form.is_wooden === null) return "Answer Wooden goods question";
      if (form.is_protein_supplement === null) return "Answer Protein supplement question";
      if (form.is_medical === null) return "Answer Medical items question";
    }
    if (form.code_mode === "manual" && !(form.manual_code || "").trim())
      return "Enter the manual shipment code";
    if (form.package_mode === "auto" && (!form.package_total || form.package_total < 1))
      return "Enter total packages (minimum 1)";
    if (!form.sales_person) return "Select a sales person";
    if (!form.job_coordinator_user_id) return "Select a job coordinator";
    if (form.sales_amount !== "" && (isNaN(Number(form.sales_amount)) || Number(form.sales_amount) < 0))
      return "Sales amount must be a positive number";
    return null;
  };

  const buildShipmentForPreview = () => ({
    ...form,
    generated_code: generatedCode,
    created_at: new Date().toISOString(),
  });

  const handleCopy = async () => {
    if (!generatedCode) {
      toast.error("Generate a code first");
      return;
    }
    const ok = await copyToClipboard(generatedCode);
    if (ok) {
      toast.success("Code copied", { description: generatedCode });
    } else {
      toast.error("Copy blocked by browser — select the code and copy manually");
    }
  };

  const handleShare = async () => {
    if (!generatedCode) {
      toast.error("Generate a code first");
      return;
    }
    const text = `Shipment Code: ${generatedCode}${form.en_no ? `\nEN NO: ${form.en_no}` : ""}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Shipment Code", text });
      } else {
        const ok = await copyToClipboard(text);
        if (ok) toast.success("Share text copied");
        else toast.error("Share failed");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing failed");
    }
  };

  const handleSave = async () => {
    const err = isFormValid();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        company_name: form.company_name.trim(),
        mobile_number: form.mobile_number.trim(),
        email: form.email.trim(),
        customer_short_code: form.customer_short_code,
        trade_lane: form.trade_lane || null,
        shipment_mode: form.shipment_mode,
        has_dg_battery: !!form.has_dg_battery,
        has_brand: !!form.has_brand,
        is_wooden: !!form.is_wooden,
        is_protein_supplement: !!form.is_protein_supplement,
        is_medical: !!form.is_medical,
        manual_code: form.code_mode === "manual" ? (form.manual_code || "").trim() : "",
        generated_code: generatedCode,
        en_no: form.en_no.trim(),
        commodity: form.commodity.trim(),
        pickup: form.pickup,
        warehouse_location: (form.warehouse_location || "").trim(),
        sales_person: form.sales_person,
        account_of_user_id: form.account_of_user_id || null,
        job_coordinator_user_id: form.job_coordinator_user_id || null,
        sales_amount: form.sales_amount === "" ? null : Number(form.sales_amount),
        cost: form.cost === "" ? null : Number(form.cost),
        margin: form.margin === "" ? null : Number(form.margin),
        sales_currency: form.sales_currency || "AED",
        cargo_lines: (form.cargo_lines || []).map((l) => ({
          package_type: l.package_type || "",
          length_cm: l.length_cm === "" || l.length_cm == null ? null : Number(l.length_cm),
          width_cm: l.width_cm === "" || l.width_cm == null ? null : Number(l.width_cm),
          height_cm: l.height_cm === "" || l.height_cm == null ? null : Number(l.height_cm),
          weight_kg: l.weight_kg === "" || l.weight_kg == null ? null : Number(l.weight_kg),
        })),
        total_weight: form.total_weight === "" || form.total_weight == null ? null : Number(form.total_weight),
        total_cbm: form.total_cbm === "" || form.total_cbm == null ? null : Number(form.total_cbm),
        total_chargeable_weight: form.total_chargeable_weight === "" || form.total_chargeable_weight == null ? null : Number(form.total_chargeable_weight),
        package_mode: form.package_mode,
        package_total: Number(form.package_total) || 1,
        package_number: form.package_number.trim(),
        label_number: (form.label_number || "").trim(),
        label_size: form.label_size,
      };
      const { data } = await api.post("/codes", payload);
      setLastSaved(data);
      toast.success("Shipment saved", { description: data.generated_code });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const err = isFormValid();
    if (err) {
      toast.error(err);
      return;
    }
    printLabelPDF(buildShipmentForPreview());
  };

  const handleDownload = () => {
    const err = isFormValid();
    if (err) {
      toast.error(err);
      return;
    }
    downloadLabelPDF(buildShipmentForPreview());
    toast.success("PDF downloaded");
  };

  const handleReset = () => {
    setForm(initialForm);
    setLastSaved(null);
    toast("Form cleared");
  };

  const totalForPreview = Math.max(1, Number(form.package_total) || 1);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <section className="lg:col-span-7 flex flex-col gap-6">
          <CodeModeCard form={form} setField={setField} />
          {form.code_mode && (
            <>
              <CustomerDetailsCard
                form={form}
                setField={setField}
                onShortCodeChange={handleShortCodeChange}
              />
              <CargoPickupCard form={form} setField={setField} />
              <SalesCard form={form} setField={setField} salesPersons={salesPersons} users={users} />
              <ShipmentModeCard form={form} setField={setField} />
              <DgBrandCards form={form} setField={setField} />
              <ExtraFlagsCard form={form} setField={setField} />
              <PackagesCard form={form} setField={setField} />
              <LabelSizeCard
                form={form}
                onPresetChange={handlePresetChange}
                onCustomSize={handleCustomSize}
              />
            </>
          )}
        </section>

        {form.code_mode && (
          <GeneratorPreviewActions
            form={form}
            generatedCode={generatedCode}
            saving={saving}
            lastSaved={lastSaved}
            totalForPreview={totalForPreview}
            onCopy={handleCopy}
            onShare={handleShare}
            onPrint={handlePrint}
            onDownload={handleDownload}
            onSave={handleSave}
            onReset={handleReset}
          />
        )}
      </div>
    </Layout>
  );
}
